import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import { useObservableAndState } from '@provider-portal/common/hooks/useObservableAndState';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';

import { MixpanelEventType } from '@provider-portal/common/services/MixpanelService';
import { PageTitle } from '@provider-portal/mp2-common/components/Page/PageTitle';

import { Tabs } from '@bolteu/clay-react';

export interface TabInfo {
  id: string;
  labelIntlId: string;
  route: string;
}

export interface TabHeaderProps {
  id: string;
  titleIntlId: string;
  tabs: TabInfo[];
}

export function TabHeader({ id, titleIntlId, tabs }: TabHeaderProps) {
  const location = useLocation();
  const intl = useIntl();
  const currentPath = location.pathname;
  const { mixpanelService, accountService } = useCommonAppServices();
  const selectedProviderId = useObservableAndState(accountService.getSelectedProviderId$());

  // Determine current active tab based on pathname
  const currentTabIndex = (() => {
    const matchingTabIndex = tabs.findIndex((tab) => {
      return currentPath === tab.route || currentPath.startsWith(`${tab.route}/`);
    });

    return matchingTabIndex === -1 ? 0 : matchingTabIndex;
  })();

  const onTabClick = (index: number) => {
    mixpanelService.trackProviderEvent(MixpanelEventType.TAB_CLICKED, {
      providerId: selectedProviderId,
      currentModuleName: id,
      currentUrl: currentPath,
      navigatedUrl: tabs[index].route,
      tabName: tabs[index].id,
      currentTabName: tabs[currentTabIndex].id,
      tabRank: index + 1,
      currentTabRank: currentTabIndex + 1,
    });
  };

  return (
    <div>
      <PageTitle title={intl.formatMessage({ id: titleIntlId })} />
      <Tabs.Root value={currentTabIndex} className="mb-3">
        <Tabs.List className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map((tab, index) => (
            <Tabs.Tab
              key={`${id}-${tab.id}`}
              value={index}
              nativeButton={false}
              render={<Link to={tab.route} />}
              onClick={() => onTabClick(index)}
              className="min-w-[90px] max-w-[360px] shrink-0 border-b-0 text-center no-underline"
            >
              {intl.formatMessage({ id: tab.labelIntlId })}
            </Tabs.Tab>
          ))}
          <Tabs.Indicator className="bg-[var(--color-content-action-secondary)]" />
        </Tabs.List>
      </Tabs.Root>
    </div>
  );
}
